import React, { useState, useEffect } from "react";

type ButtonProps = {
    roomName: string;
    onSelect: (data: SelectedRooms | null) => void;
    defaultCount?: number;
    disabled?: boolean;
    onError: (message: string) => void;
    hideRemoveButton?: boolean;
    value?: [];
};

type SelectedRooms = {
    roomName: string;
    count: number;
    value?: [];
};

type RoomsProps = {
    onSelectedRoomsChange: (selectedRooms: SelectedRooms[]) => void;
    selectedRoomsParent: SelectedRooms[];
    disabled?: boolean;
    setIsFormDirty: any;
};

const ToggleButton: React.FC<ButtonProps> = ({ roomName, onSelect, defaultCount = 0, disabled = false, hideRemoveButton = false, onError, value }) => {
    const [isSelected, setIsSelected] = useState<boolean>(defaultCount > 0);
    const [count, setCount] = useState<number>(defaultCount);

    const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setIsSelected(!isSelected);
        if (isSelected) {
            setCount(0);
            onSelect(null);
            onError("");
        } else {
            setCount(1);
            onSelect({ roomName, count: 1, value });
        }
    };

    const handleIncrement = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        if (count < 10) {
            const newCount = count + 1;
            setCount(newCount);
            onSelect({ roomName, count: newCount, value });
            onError("");
        } else {
            onError("Maximum count of 10 reached.");
            setTimeout(() => onError(''), 3000);
        }
    };

    const handleDecrement = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        if (count > 1) {
            const newCount = count - 1;
            setCount(newCount);
            onSelect({ roomName, count: newCount, value });
            onError("");
        } else if (count === 1) {
            setIsSelected(false);
            setCount(0);
            onSelect({ roomName, count: 0, value });
            onError("");
        }
    };

    return (
        <div
            className={`flex items-center cursor-pointer justify-center gap-20 ${isSelected ? "bg-black text-white px-[18px] py-[4.5px] rounded-full" : ""
                }`}
        >
            {isSelected ? (
                <>
                    <span
                        className={`text-lg ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
                    >
                        ✓
                    </span>
                    {!hideRemoveButton && (
                        <button
                            className="text-4xl text-white rounded-full w-6 h-6 flex items-center justify-center cursor-pointer"
                            onClick={handleDecrement}
                            disabled={disabled}
                        >
                            -
                        </button>
                    )}
                    <span className="fontSize14-sm">{`${roomName}  x  ${count}`}</span>
                    <button
                        className="text-xl text-white rounded-full w-6 h-6 flex items-center justify-center cursor-pointer"
                        onClick={handleIncrement}
                        disabled={disabled}
                    >
                        +
                    </button>
                </>
            ) : (
                <button
                    className="border-2	text-black fontSize14-sm px-18 py-[4.5px] rounded-full"
                    onClick={handleToggle}
                    disabled={disabled}
                >
                    {roomName}
                </button>
            )}
        </div>
    );
};

const RoomsDetails: React.FC<RoomsProps> = ({ onSelectedRoomsChange, setIsFormDirty, selectedRoomsParent, disabled = false }) => {
    const [rooms, setRooms] = useState<string[]>([
        "Main bathroom",
        "Ensuite Bathroom",
        "Guest Bathroom",
        "Powder Room",
        "Commercial Bathroom",
        "Kitchen",
        "Laundry",
        "Alfresco",
        "Other",
    ]);
    const [selectedRooms, setSelectedRooms] = useState<SelectedRooms[]>([]);
    const [showInput, setShowInput] = useState(false);
    const [newRoom, setNewRoom] = useState("");
    const [key, setKey] = useState<number>(0);
    const [errorMessage, setErrorMessage] = useState<string>("");

    useEffect(() => {
        onSelectedRoomsChange(selectedRooms);
    }, [selectedRooms, onSelectedRoomsChange]);

    useEffect(() => {
        if (selectedRoomsParent?.length) {
            setSelectedRooms([...selectedRoomsParent]);
            const newRooms = selectedRoomsParent
                .map((room) => room.roomName)
                .filter((roomName) => !rooms.includes(roomName));
            
            if (newRooms.length) {
                setRooms((prevRooms) => {
                    const withoutOther = prevRooms.filter(room => room !== "Other");
                    return [...withoutOther, ...newRooms, "Other"];
                });
            }
            setKey((prevKey) => prevKey + 1);
        }
    }, [selectedRoomsParent]);

    const handleSelect = (data: SelectedRooms | null) => {
        setErrorMessage('');
        setIsFormDirty(true);
        if (data && data?.count > 0) {
            setSelectedRooms((prev) => {
                const existing = prev.find((item) => item.roomName === data.roomName);
                if (existing) {
                    return prev.map((item) =>
                        item.roomName === data.roomName ? data : item
                    );
                }
                return [...prev, data];
            });
        } else if (data) {
            setSelectedRooms((prev) =>
                prev.filter((item) => item.roomName !== data.roomName)
            );
        }
    };

    const handleOtherSelect = () => {
        setShowInput(true);
    };

    const handleAddButton = () => {
        setErrorMessage("")
        if (newRoom.trim() !== "" && !rooms.includes(newRoom.trim())) {
            setRooms((prev) => [...prev.filter((b) => b !== "Other"), newRoom.trim(), "Other"]);
            setShowInput(false);
            setNewRoom("");
        }else if(rooms.includes(newRoom.trim())){
            setErrorMessage("Room already created")
        }
    };

    const handleInputCancel = () => {
        setErrorMessage("")
        setShowInput(false);
        setNewRoom("");
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const regex = /[^a-zA-Z0-9\s]/g;
        const sanitizedValue = e.target.value.replace(regex, '');
        setNewRoom(sanitizedValue);
    };

    return (
        <div className="p-6 space-y-4">
            <div className="flex flex-wrap gap-[1.5rem]">
                {rooms.map((roomName) => {
                    const defaultCount =
                        selectedRooms.find((item) => item.roomName === roomName)?.count || 0;

                    const preSelectedRoom =
                        selectedRooms.find((item) => item.roomName === roomName)?.value || [];

                    const hideRemoveButton =
                        selectedRoomsParent.some(
                            (room) => room.roomName === roomName
                        );

                    return roomName === "Other" ? (
                        <div key={roomName}>
                            {showInput ? (
                                <div className="items-center space-x-2">
                                    <input
                                        type="text"
                                        className="border px-[18px] py-[9px] rounded mb-10"
                                        placeholder="Enter new room"
                                        value={newRoom}
                                        onChange={handleChange}
                                        maxLength={30}
                                    />
                                    <button
                                        className="text-green-600 bg-white border p-[10px] rounded  mb-10"
                                        onClick={handleAddButton}
                                        type="button"
                                    >
                                        Create room
                                    </button>
                                    <button
                                        className="p-[10px] bg-gray-200 border rounded  mb-10"
                                        onClick={handleInputCancel}
                                        type="button"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <ToggleButton
                                    roomName={roomName}
                                    onSelect={() => handleOtherSelect()}
                                    disabled={disabled}
                                    onError={setErrorMessage}
                                    value={preSelectedRoom}
                                />
                            )}
                        </div>
                    ) : (
                        <ToggleButton
                            key={`${roomName}-${key}`}
                            roomName={roomName}
                            onSelect={handleSelect}
                            defaultCount={defaultCount}
                            disabled={disabled}
                            hideRemoveButton={hideRemoveButton}
                            onError={setErrorMessage}
                            value={preSelectedRoom}
                        />
                    );
                })}
            </div>
            {errorMessage && <div className="text-red-500 text-sm">{errorMessage}</div>}
        </div>
    );
};

export default RoomsDetails;
